import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AgendaDataService,
  AgendaService,
  BardoContextDataService,
  EstabelecimentoDataService,
  NichoTermosService,
  ProfissionalDataService,
  TipoServicoDataService,
} from '@nomoh-monorepo/data-access-bardo-administrador';

import { FormsModule } from '@angular/forms';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import {
  BottomSheetComponent,
  DeviceService,
  FormFieldComponent,
  NMultiselectComponent,
  PhoneInputComponent,
} from '@nomoh-monorepo/shared-common-ui';
import {
  IAgendamento,
  IClienteDependenteEntity,
  IEstabelecimentoClienteEntity,
  IProfissional,
  IServico,
  ITipoServico,
  StatusAgendamentoEnum,
} from '@nomoh-monorepo/shared-types';
import { LuxonNomoh } from '@nomoh-monorepo/shared-util-luxon-nomoh';
import { MaskUtils, toInstantDateTime } from '@nomoh-monorepo/shared-util-tools';
import { DateTime } from 'luxon';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { DialogService } from 'primeng/dynamicdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';

type SlotStatus =
  | 'disponivel'
  | 'reservado'
  | 'conflito'
  | 'almoco'
  | 'ausente';

interface KeyValueOption {
  label: string;
  value: string;
  duracao?: string;
  active?: boolean | null;
  slotStatus?: SlotStatus;
  conflitoCom?: string;
  conflitoLabel?: string;
  statusLabel?: string;
}

type TipoServicoComServicos = ITipoServico & { servicos?: IServico[] };
type DateInput = Date | string | DateTime | null | undefined;
type NovoAgendamentoPayload = Parameters<
  AgendaService['inserirAgendamento']
>[0];

@Component({
  selector: 'lib-agenda-novo-agendamento',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    SelectModule,
    InputTextModule,
    FormFieldComponent,
    ConfirmDialogModule,
    DatePickerModule,
    DialogModule,
    TextareaModule,
    TooltipModule,
    InputGroupAddonModule,
    InputGroupModule,
    IconFieldModule,
    InputIconModule,
    MessageModule,
    NMultiselectComponent,
    BottomSheetComponent,
    PhoneInputComponent,
    TranslateModule,
  ],

  templateUrl: './agenda-novo-agendamento.component.html',
  styleUrl: './agenda-novo-agendamento.component.scss',
  providers: [DialogService],
})
export class AgendaNovoAgendamentoComponent
  implements OnInit, OnDestroy, AfterViewInit {
  private readonly timeZonePadrao = 'America/Sao_Paulo';

  //#region Injects
  private fb = inject(FormBuilder);
  private profissionalDataService = inject(ProfissionalDataService);
  private bardoContextDataService = inject(BardoContextDataService);
  private notificacaoService = inject(NotificacaoService);
  private confirmationService = inject(ConfirmationService);
  private translateService = inject(TranslateService);
  tipoServicoDataService = inject(TipoServicoDataService);
  AgendaService = inject(AgendaService);
  AgendaDataService = inject(AgendaDataService);
  deviceService = inject(DeviceService);
  estabelecimentoDataService = inject(EstabelecimentoDataService);
  $nicho = inject(NichoTermosService).$nicho;
  //#endregion


  //#region signals
  $isLoading = signal(false);
  $continuarAgendamento = signal(false);
  $desabilitarBotaoAdicionarCliente = signal(true);
  $modalSeletorHorarioVisible = signal(false);
  horarioTemporarioEncaixe: Date = new Date();
  $dropdownAppendTo = computed(() =>
    this.deviceService.$isMobile() ? undefined : 'body'
  );

  $diasDesabilitados = computed(() => {
    const est = this.estabelecimentoDataService.$estabelecimentoAtual();
    if (!est?.diasFuncionamento?.length) return [];
    // diaId: 1=Domingo...7=Sábado → JS getDay(): 0=Domingo...6=Sábado → (diaId-1)%7
    const diasAbertos = new Set(
      (est.diasFuncionamento as Array<{ diaId: number }>).map(
        (d) => (d.diaId - 1) % 7
      )
    );
    return [0, 1, 2, 3, 4, 5, 6].filter((dia) => !diasAbertos.has(dia));
  });

  $feriadosDesabilitados = computed(() => {
    const est = this.estabelecimentoDataService.$estabelecimentoAtual();
    if (!est || est.abreFeriado) return [];
    return this.AgendaDataService.$feriados()
      .filter((f) => f?.date)
      .map((f) => new Date(f.date + 'T00:00:00'));
  });

  private _servicoSelecionadoId = signal<string | null>(null);
  private _profissionalSelecionadoId = signal<string | null>(null);
  private _dataSelecionada = signal<Date | null>(null);

  $estabelecimentoFechado = computed(() => {
    const dataSelecionada = this._dataSelecionada();
    if (!dataSelecionada) return false;
    const agora = this.dateTimeNow();
    const dataForm = this.toCalendarDateTime(dataSelecionada);
    if (!agora.hasSame(dataForm, 'day')) return false;
    const est = this.estabelecimentoDataService.$estabelecimentoAtual();
    if (!est?.horaAbertura || !est?.horaFechamento) return false;
    const [hAbertura, mAbertura] = est.horaAbertura.split(':').map(Number);
    const [hFechamento, mFechamento] = est.horaFechamento
      .split(':')
      .map(Number);
    const abertura = agora.set({
      hour: hAbertura,
      minute: mAbertura,
      second: 0,
    });
    const fechamento = agora.set({
      hour: hFechamento,
      minute: mFechamento,
      second: 0,
    });
    return agora < abertura || agora >= fechamento;
  });

  $semProfissionalDisponivel = computed(() => {
    const servicoId = this._servicoSelecionadoId();
    if (!servicoId) return false;
    return this.profissionaisFiltrados.length === 0;
  });

  $profissionalComAusencia = computed(() => {
    const profissionalId = this._profissionalSelecionadoId();
    if (!profissionalId) return false;
    const profissional = this.profissionalDataService
      .$profissionais()
      .find((p) => String(p.id) === profissionalId);
    if (!profissional?.diasAusentes?.length) return false;
    const dataRaw = this.agendamentoForm?.get('data')?.value;
    if (!dataRaw) return false;
    const dataSelecionada = this.toCalendarDateTime(dataRaw);
    return profissional.diasAusentes.some((aus) => {
      const ausStart = LuxonNomoh.convertToUtc(aus.dataHoraInicio);
      const ausEnd = LuxonNomoh.convertToUtc(aus.dataHoraFim);
      const diaStart = dataSelecionada.startOf('day').toUTC();
      const diaEnd = dataSelecionada.endOf('day').toUTC();
      return ausStart < diaEnd && ausEnd > diaStart;
    });
  });

  //#endregion

  //#region propriedades
  $opcoesPacientes = signal<Array<{ label: string; value: string }>>([]);
  opcoesDomicilio: Array<{ label: string; value: boolean }> = [];
  agendamentoForm!: FormGroup;
  tiposServicos: KeyValueOption[] = [];
  servicosFiltrados: KeyValueOption[] = [];
  profissionaisFiltrados: KeyValueOption[] = [];
  horariosDisponiveis: KeyValueOption[] = [];
  dataMinima: Date = new Date(new Date().setHours(0, 0, 0, 0));
  private profissionais: IProfissional[] = [];
  private formSubscriptions = new Subscription();
  private enderecoClienteSelecionado: string | null = null;

  private $isReagendamento = signal(false);
  $labelButtonSalvar = computed(() => {
    if (this.isEncaixe) {
      return (
        (this.translateService.instant('BARDO_AGENDA.SALVAR_ENCAIXE') as string) ||
        'Salvar Encaixe'
      );
    }
    if (this.$isReagendamento()) {
      return (
        (this.translateService.instant('BARDO_AGENDA.CONFIRMAR_REAGENDAMENTO') as string) ||
        'Confirmar Reagendamento'
      );
    }
    return (
      (this.translateService.instant('BARDO_AGENDA.SALVAR_AGENDAMENTO', {
        termo: this.$nicho().agendamento.singular,
      }) as string) || ('Salvar ' + this.$nicho().agendamento.singular)
    );
  });
  //#endregion


  //#region ViewChild
  @ViewChild('nomeInput') nomeInput!: ElementRef<HTMLInputElement>;
  @ViewChild('headerTemplate', { static: false })
  headerTemplateRef?: TemplateRef<unknown>;
  @ViewChild('footerTemplate', { static: false })
  footerTemplateRef!: TemplateRef<unknown>;
  //#endregion

  //#region Inputs e Outputs
  @Input() agendamento: IAgendamento = {} as IAgendamento;
  private _cliente?: IEstabelecimentoClienteEntity | null;
  @Input() set cliente(val: IEstabelecimentoClienteEntity | null | undefined) {
    this._cliente = val;
    if (val && val.id) {
      this.carregarClienteEDependentes(val);
    }
  }
  get cliente(): IEstabelecimentoClienteEntity | null | undefined {
    return this._cliente;
  }
  /**
   * Quando `true`, cria um encaixe (agendamento extraordinário) via rota
   * dedicada, ignorando a validação de disponibilidade/conflito de horário
   * no backend. O aviso de conflito no formulário continua sendo exibido
   * normalmente — o usuário decide se mantém a seleção.
   */
  @Input() isEncaixe = false;
  @Output() headerTemplateAction = new EventEmitter<TemplateRef<unknown>>();
  @Output() footerTemplateAction = new EventEmitter<TemplateRef<unknown>>();
  @Output() loadingChange = new EventEmitter<boolean>();
  @Output() agendamentoSalvo = new EventEmitter<void>();
  //#endregion

  constructor() {
    // Effect: Sincronizar data selecionada com formulário
    effect(() => {
      const dataSelecionada = this.AgendaDataService.$dataSelecionada();
      if (dataSelecionada && this.agendamentoForm) {
        this.atualizarDataForm(dataSelecionada);
      }
    });

    effect(() => {
      const tiposServicos = this.tipoServicoDataService
        .$tiposServico() as TipoServicoComServicos[];
      const profissionais = this.profissionalDataService.$profissionais();
      const meuProfissionalId = this.bardoContextDataService.$meuProfissionalId();
      const meusServicoIds = this.calcularMeusServicoIds(
        profissionais,
        meuProfissionalId
      );

      // Perfil PROFISSIONAL só pode agendar consigo mesmo (ver
      // $meuProfissionalId): exibir categorias sem nenhum serviço dele leva
      // a um beco sem saída (serviço selecionável, mas nenhum profissional
      // disponível na etapa seguinte). Filtramos aqui para nunca chegar lá.
      const tiposDisponiveis = meusServicoIds
        ? tiposServicos.filter((tipo) =>
          (tipo.servicos || []).some((servico) =>
            meusServicoIds.has(String(servico.id))
          )
        )
        : tiposServicos;

      this.tiposServicos = tiposDisponiveis
        .map((tipo) => ({
          label: tipo.nome,
          value: String(tipo.id),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    });

    effect(() => {
      this.profissionais = this.profissionalDataService.$profissionais();
    });

  }

  ngOnInit(): void {
    this.inicializarForm();
    this.atualizarOpcoesDomicilio();
    this.configurarListeners();
    this.sincronizarDataSelecionada();
    if (this.cliente && this.cliente.id && !this.agendamento?.agendaId) {

      this.carregarClienteEDependentes(this.cliente);
    }
  }

  ngAfterViewInit(): void {
    if (this.headerTemplateRef) {
      this.headerTemplateAction.emit(this.headerTemplateRef);
    }

    if (this.footerTemplateRef) {
      this.footerTemplateAction.emit(this.footerTemplateRef);
    }

    if (this.agendamento?.agendaId || this.agendamento?.id) {
      this.carregarAgendamentoParaEdicao();
    }
  }

  ngOnDestroy(): void {
    this.formSubscriptions.unsubscribe();
  }

  //#region Inicialização
  inicializarForm(): void {
    this.agendamentoForm = this.fb.group({
      tipoServico: [null, Validators.required],
      servico: [{ value: null, disabled: true }, Validators.required],
      profissional: [{ value: null, disabled: true }, Validators.required],
      data: ['', Validators.required],
      horario: [{ value: null, disabled: true }, Validators.required],
      nome: [{ value: null, disabled: true }, Validators.required],
      telefone: [
        { value: null, disabled: true },
        [Validators.required],
      ],
      clientId: [{ value: null, disabled: true }, Validators.required],
      pacienteId: ['titular'],
      nomePacienteDependente: [null],
      dataNascimentoPacienteDependente: [null],
      id: [0],
      observacoes: [null],
      isDomicilio: [false, Validators.required],
      endereco: [null, Validators.maxLength(500)],
    });
  }

  configurarListeners(): void {
    this.formSubscriptions.unsubscribe();
    this.formSubscriptions = new Subscription();
    this.formSubscriptions.add(
      this.translateService.onLangChange.subscribe(() =>
        this.atualizarOpcoesDomicilio()
      )
    );

    const tipoServicoControl = this.agendamentoForm.get('tipoServico');
    const servicoControl = this.agendamentoForm.get('servico');
    const profissionalControl = this.agendamentoForm.get('profissional');
    const horarioControl = this.agendamentoForm.get('horario');
    const dataControl = this.agendamentoForm.get('data');
    const domicilioControl = this.agendamentoForm.get('isDomicilio');

    if (domicilioControl) {
      this.formSubscriptions.add(
        domicilioControl.valueChanges.subscribe((isDomicilio) => {
          this.atualizarValidacaoEndereco(isDomicilio === true, true);
        })
      );
    }

    if (dataControl) {
      this.formSubscriptions.add(
        dataControl.valueChanges.subscribe((data) => {
          let dataObj: Date | null = null;
          if (data instanceof Date) {
            dataObj = data;
          } else if (typeof data === 'string' && data) {
            // "YYYY-MM-DD" from native date input — use midday to avoid timezone shifts
            dataObj = new Date(data + 'T12:00:00');
          }
          this._dataSelecionada.set(dataObj);
          if (servicoControl?.value) {
            servicoControl.setValue(servicoControl.value);
          }
          horarioControl?.disable();
          horarioControl?.reset();
        })
      );
    }

    if (tipoServicoControl) {
      this.formSubscriptions.add(
        tipoServicoControl.valueChanges.subscribe((tipoId) => {
          const tiposServicos =
            this.tipoServicoDataService.$tiposServico() as TipoServicoComServicos[];

          const tipoServicoSelecionado = tiposServicos.find(
            (tipo) => String(tipo.id) === String(tipoId)
          );

          const meusServicoIds = this.calcularMeusServicoIds(
            this.profissionais,
            this.bardoContextDataService.$meuProfissionalId()
          );

          this.servicosFiltrados = (tipoServicoSelecionado?.servicos || [])
            .filter((servico) => servico.status !== false)
            .filter(
              (servico) =>
                !meusServicoIds || meusServicoIds.has(String(servico.id))
            )
            .map((servico) => ({
              label: servico.nome,
              value: String(servico.id),
              duracao: this.formatarDuracaoLabel(servico.duracao),
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

          if (this.servicosFiltrados.length) {
            servicoControl?.enable();
            servicoControl?.reset();
          } else {
            this.servicosFiltrados = [];
            servicoControl?.disable();
            servicoControl?.reset();
          }

          this.profissionaisFiltrados = [];
          this._servicoSelecionadoId.set(null);
          this._profissionalSelecionadoId.set(null);
          profissionalControl?.disable();
          profissionalControl?.reset();
          this.horariosDisponiveis = [];
          horarioControl?.disable();
          horarioControl?.reset();
        })
      );
    }

    if (servicoControl) {
      this.formSubscriptions.add(
        servicoControl.valueChanges.subscribe((servicoId) => {
          const servicoSelecionadoId =
            servicoId == null ? null : String(servicoId);
          if (!servicoSelecionadoId) {
            this.profissionaisFiltrados = [];
            profissionalControl?.disable();
            profissionalControl?.reset();
            this.horariosDisponiveis = [];
            horarioControl?.disable();
            horarioControl?.reset();
            return;
          }

          const dataSelecionada = this.agendamentoForm.get('data')?.value;
          if (!dataSelecionada) {
            this.notificacaoService.notificacaoWarn(
              'Selecione uma data antes de escolher o serviço.'
            );
            servicoControl?.reset();
            return;
          }

          const dataSelecionadaLocal =
            this.toCalendarDateTime(dataSelecionada);
          const diaSemanaPadronizado =
            dataSelecionadaLocal.weekday === 7
              ? 1
              : dataSelecionadaLocal.weekday + 1;

          let profissionaisDisponiveis = this.profissionais.filter(
            (profissional) => {
              const atendeServico = (profissional.servicos || []).some(
                (servico) => String(servico.servicoId) === servicoSelecionadoId
              );
              const presenteNoDia = (
                profissional.diasSemanaPresente || []
              ).some(
                (dia) => String(dia.diaId) === String(diaSemanaPadronizado)
              );

              // Removemos o filtro de 'ausenteNoDia' que bloqueava o dia inteiro.
              // A verificação de ausência agora é feita slot a slot na geração de horários.
              return !profissional.ferias && atendeServico && presenteNoDia;
            }
          );

          // Um usuário com perfil PROFISSIONAL só pode agendar consigo mesmo
          // (o backend também valida isso — aqui é só para não deixar
          // selecionar um colega e depois falhar ao salvar).
          const meuProfissionalId = this.bardoContextDataService.$meuProfissionalId();
          if (meuProfissionalId) {
            profissionaisDisponiveis = profissionaisDisponiveis.filter(
              (profissional) => String(profissional.id) === meuProfissionalId
            );
          }

          this.profissionaisFiltrados = profissionaisDisponiveis
            .map((profissional) => ({
              label: profissional.nome,
              value: String(profissional.id),
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

          this._servicoSelecionadoId.set(servicoSelecionadoId);

          if (this.profissionaisFiltrados.length) {
            profissionalControl?.enable();
            profissionalControl?.reset();
            if (meuProfissionalId) {
              // setValue já dispara profissionalControl.valueChanges, que gera
              // os horários disponíveis e habilita horarioControl — não pode
              // ser sobrescrito pelo reset abaixo.
              profissionalControl?.setValue(meuProfissionalId);
              profissionalControl?.disable();
              return;
            }
          } else {
            profissionalControl?.disable();
            profissionalControl?.reset();
          }

          this.horariosDisponiveis = [];
          horarioControl?.disable();
          horarioControl?.reset();
        })
      );
    }

    if (profissionalControl) {
      this.formSubscriptions.add(
        profissionalControl.valueChanges.subscribe((profissionalId) => {
          const profissionalSelecionadoId =
            profissionalId == null ? null : String(profissionalId);

          this._profissionalSelecionadoId.set(profissionalSelecionadoId);

          if (profissionalSelecionadoId) {
            // Encaixe usa input de horário livre (sem grade de 30min) — não
            // faz sentido gerar/validar contra slots pré-calculados.
            if (!this.isEncaixe) {
              this.gerarHorariosDisponiveis(profissionalSelecionadoId);
            }
            horarioControl?.enable();
          } else {
            this.horariosDisponiveis = [];
            horarioControl?.disable();
            horarioControl?.reset();
          }
        })
      );
    }
  }

  private async carregarAgendamentoParaEdicao(): Promise<void> {
    const agendamento = this.agendamento;
    if (!agendamento) return;

    if (!agendamento.responsavelNome && agendamento.clienteId && !agendamento.telefoneCliente) {
      try {
        const vinculos = await this.AgendaService.buscarResponsaveisDe(agendamento.clienteId);
        const listaVinculos = Array.isArray(vinculos)
          ? vinculos
          : ((vinculos as unknown as { data: IClienteDependenteEntity[] })?.data || []);
        if (listaVinculos && listaVinculos.length > 0 && listaVinculos[0].responsavel) {
          agendamento.responsavelNome = listaVinculos[0].responsavel.nome;
          agendamento.responsavelTelefone = listaVinculos[0].responsavel.telefone;
        }
      } catch (err) {
        console.error('Erro ao buscar responsavel do agendamento', err);
      }
    }

    const tiposServicos =
      this.tipoServicoDataService.$tiposServico() as TipoServicoComServicos[];
    let tipoServicoId: string | null = null;
    this.$isReagendamento.set(true);


    for (const tipo of tiposServicos) {
      const temServico = (tipo.servicos || []).some(
        (s) => String(s.id) === String(agendamento.servicoId)
      );
      if (temServico) {
        tipoServicoId = String(tipo.id);
        break;
      }
    }

    const dataObj = this.toInstantDateTime(agendamento.dataHora);
    const dataCampo = this.toBrowserCalendarDate(dataObj);

    const telefoneParaForm = agendamento.responsavelTelefone || agendamento.telefoneCliente || '';

    this.agendamentoForm.patchValue(
      {
        id: agendamento.id,
        clientId: agendamento.clienteId,
        nome: agendamento.nomeCliente,
        telefone: telefoneParaForm,
        data: dataCampo,
        observacoes: agendamento.observacao,
        isDomicilio: agendamento.isDomicilio ?? false,
        endereco: agendamento.endereco ?? null,
      },
      { emitEvent: false }
    );

    this.atualizarValidacaoEndereco(agendamento.isDomicilio === true, false);

    this.agendamentoForm.get('nome')?.disable();
    this.agendamentoForm.get('telefone')?.disable();
    this.$desabilitarBotaoAdicionarCliente.set(true);
    this.$continuarAgendamento.set(true);

    if (tipoServicoId) {
      this.agendamentoForm.get('tipoServico')?.setValue(tipoServicoId);

      setTimeout(() => {
        this.agendamentoForm
          .get('servico')
          ?.setValue(String(agendamento.servicoId));
        setTimeout(() => {
          this.agendamentoForm
            .get('profissional')
            ?.setValue(String(agendamento.profissionalId));
          setTimeout(() => {
            this.agendamentoForm
              .get('horario')
              ?.setValue(dataObj.toFormat('HH:mm'));
          }, 0);
        }, 0);
      }, 0);
    }
  }
  //#endregion

  async carregarClienteEDependentes(cliente: IEstabelecimentoClienteEntity): Promise<void> {
    if (!cliente || !cliente.id) return;

    this.enderecoClienteSelecionado = cliente.endereco?.trim() || null;

    let clienteAlvo = cliente;
    let idDependenteSelecionar: string | null = null;

    let responsavelId = cliente.responsavelId;
    let responsavelNome = cliente.responsavelNome;
    let responsavelTelefone = cliente.responsavelTelefone;

    if ((!responsavelId || !responsavelNome || !responsavelTelefone) && !cliente.telefone) {
      try {
        const vinculos = await this.AgendaService.buscarResponsaveisDe(cliente.id);
        const listaVinculos = Array.isArray(vinculos)
          ? vinculos
          : ((vinculos as unknown as { data: IClienteDependenteEntity[] })?.data || []);
        if (listaVinculos && listaVinculos.length > 0 && listaVinculos[0].responsavel) {
          responsavelId = listaVinculos[0].responsavel.id;
          responsavelNome = listaVinculos[0].responsavel.nome;
          responsavelTelefone = listaVinculos[0].responsavel.telefone;
        }
      } catch (err) {
        console.error('Erro ao buscar responsavel do cliente', err);
      }
    }

    if (responsavelId && (responsavelNome || responsavelTelefone)) {
      idDependenteSelecionar = cliente.id;
      clienteAlvo = {
        id: responsavelId,
        nome: responsavelNome || 'Responsável',
        telefone: responsavelTelefone,
        estabelecimentoId: cliente.estabelecimentoId,
      };
    }

    this.agendamento.clienteId = clienteAlvo.id!;
    this.agendamento.nomeCliente = clienteAlvo.nome;
    this.agendamento.telefoneCliente = clienteAlvo.telefone;

    if (this.agendamentoForm) {
      this.agendamentoForm.patchValue({
        nome: clienteAlvo.nome,
        telefone: clienteAlvo.telefone ?? '',
        clientId: clienteAlvo.id,
      });
      this.$desabilitarBotaoAdicionarCliente.set(true);
      this.$continuarAgendamento.set(true);
    }

    try {
      const dependentes = await this.AgendaService.buscarDependentesDe(clienteAlvo.id!);
      const opcoes: Array<{ label: string; value: string }> = [
        {
          label: this.translateService.instant('BARDO_CLIENTES.PARA_MIM', {
            nome: clienteAlvo.nome,
          }),
          value: 'titular',
        },
      ];

      for (const dep of dependentes) {
        if (dep.cliente) {
          opcoes.push({
            label: `${this.translateService.instant('BARDO_CLIENTES.DEPENDENTE')}: ${dep.cliente.nome}`,
            value: `paciente_${dep.cliente.id}`,
          });
        }
      }

      opcoes.push({
        label: this.translateService.instant('BARDO_CLIENTES.CADASTRAR_NOVO_DEPENDENTE', {
          termo: this.$nicho().cliente.singular,
        }),
        value: 'novo_paciente',
      });


      this.$opcoesPacientes.set(opcoes);

      if (this.agendamentoForm) {
        const valorPaciente = idDependenteSelecionar
          ? `paciente_${idDependenteSelecionar}`
          : 'titular';
        this.agendamentoForm.get('pacienteId')?.setValue(valorPaciente);
      }
    } catch (error) {
      console.error('Erro ao carregar dependentes do cliente', error);
    }
  }

  //#region Busca Cliente
  onTelefoneChange(value: string): void {
    const ctrl = this.agendamentoForm.get('telefone');
    ctrl?.setValue(value);
    ctrl?.markAsDirty();
    ctrl?.markAsTouched();
  }

  onPhoneFocusOut(event: FocusEvent): void {
    const related = event.relatedTarget as HTMLElement | null;
    if (!related?.closest?.('lib-phone-input')) {
      void this.buscarCliente();
    }
  }

  async buscarCliente(): Promise<void> {
    const telefone = this.agendamentoForm.get('telefone')?.value ?? '';

    if (!telefone || !this.validarTelefone(telefone)) {
      return;
    }

    const telefoneLimpo = this.limparTelefone(telefone);

    this.setLoading(true);
    this.$continuarAgendamento.set(false);

    try {
      const cliente = await this.AgendaService.buscarClientePorTelefone(
        telefoneLimpo
      );

      if (cliente) {
        await this.carregarClienteEDependentes(cliente as any);
        this.notificacaoService.notificacaoSuccess('Cliente encontrado!');
        return;
      }

      this.agendamentoForm.get('nome')?.reset();
      this.agendamentoForm.patchValue({ id: 0 });
      this.agendamentoForm.get('nome')?.enable();
      this.$desabilitarBotaoAdicionarCliente.set(false);
      this.notificacaoService.notificacaoWarn(
        'Cliente não encontrado. Informe o nome para cadastro rápido.'
      );

      setTimeout(() => {
        this.nomeInput?.nativeElement?.focus();
      }, 0);
    } catch {
      // interceptor handles error notification
    } finally {
      this.setLoading(false);
    }
  }

  async novoUsuarioRapido(): Promise<void> {
    this.setLoading(true);

    const { nome, telefone } = this.agendamentoForm.value;

    if (!nome) {
      this.nomeInput.nativeElement.focus();
      this.setLoading(false);
      return;
    }

    if (!telefone || !this.validarTelefone(telefone)) {
      this.notificacaoService.notificacaoWarn(
        'Informe um telefone válido para cadastrar o cliente.'
      );
      this.setLoading(false);
      return;
    }

    try {
      const telefoneLimpo = this.limparTelefone(telefone);
      const cliente = await this.AgendaService.cadastrarClienteRapido({
        nome,
        telefone: telefoneLimpo,
      });

      if (!cliente) {
        throw new Error('Cadastro rápido não retornou cliente');
      }

      this.agendamentoForm.patchValue({ id: cliente.id, nome: cliente.nome });
      this.agendamentoForm.get('nome')?.disable();
      this.$desabilitarBotaoAdicionarCliente.set(true);
      this.$continuarAgendamento.set(true);

    } catch {
      // interceptor handles error notification
    } finally {
      this.setLoading(false);
    }
  }
  //#endregion

  //#region Horários
  onHorarioChange(event: { value: string }): void {
    const horarioSelecionado = this.horariosDisponiveis.find(
      (h) => h.value === event.value
    );

    if (
      horarioSelecionado?.slotStatus &&
      horarioSelecionado.slotStatus !== 'disponivel'
    ) {
      const mensagemAlmoco =
        horarioSelecionado.slotStatus === 'almoco'
          ? 'Intervalo de almoço. Deseja manter a seleção e agendar mesmo assim?'
          : undefined;
      this.confirmationService.confirm({
        message:
          mensagemAlmoco ??
          'Este horário apresenta um conflito. Deseja manter a seleção e agendar mesmo assim?',
        header: 'Atenção',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Sim, agendar',
        rejectLabel: 'Cancelar',
        acceptButtonStyleClass: 'p-button-success',
        reject: () => {
          this.agendamentoForm.get('horario')?.reset();
        },
      });
    }
  }

  onHorarioNativeChange(value: string): void {
    this.onHorarioChange({ value });
  }

  formatarServicoMobileLabel(
    servico: KeyValueOption & { duracao?: string }
  ): string {
    return servico.duracao
      ? `${servico.label} (${servico.duracao})`
      : servico.label;
  }

  formatarHorarioMobileLabel(horario: KeyValueOption): string {
    if (horario.statusLabel) {
      return `${horario.label} - ${horario.statusLabel}`;
    }
    if (horario.conflitoLabel) {
      return `${horario.label} - ${horario.conflitoLabel}`;
    }
    return horario.label;
  }

  aplicarMascaraData(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    if (!inputElement || !inputElement.value) return;

    // Apenas se for um input text
    if (inputElement.tagName !== 'INPUT') return;

    const valorFormatado = MaskUtils.formatarData(inputElement.value);

    // Se o valor já for igual, não faz nada para evitar loop infinito da DOM
    if (inputElement.value !== valorFormatado) {
      inputElement.value = valorFormatado;
      inputElement.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  private gerarHorariosDisponiveis(profissionalId: string): void {
    const profissional = this.profissionais.find(
      (p) => String(p.id) === profissionalId
    );

    if (
      !profissional ||
      !profissional.inicioExpediente ||
      !profissional.fimExpediente
    ) {
      this.notificacaoService.notificacaoWarn(
        'Não foi possível determinar o expediente do profissional selecionado.'
      );
      this.horariosDisponiveis = [];
      return;
    }

    const dataSelecionadaRaw =
      this.agendamentoForm.get('data')?.value ||
      this.AgendaDataService.$dataSelecionada();
    if (!dataSelecionadaRaw) {
      this.notificacaoService.notificacaoWarn(
        'Selecione uma data para gerar os horários disponíveis.'
      );
      return;
    }

    const servicoId = this.agendamentoForm.get('servico')?.value;
    const tiposServicos =
      this.tipoServicoDataService.$tiposServico() as TipoServicoComServicos[];
    let duracaoNovoServico = 30;
    for (const tipo of tiposServicos) {
      const servico = tipo.servicos?.find(
        (s) => String(s.id) === String(servicoId)
      );
      if (servico) {
        duracaoNovoServico = this.parseDuracaoParaMinutos(servico.duracao);
        break;
      }
    }

    const [hInicio, mInicio] = profissional.inicioExpediente
      .split(':')
      .map(Number);
    const [hFim, mFim] = profissional.fimExpediente.split(':').map(Number);

    const dataSelecionada = this.toCalendarDateTime(dataSelecionadaRaw);
    const agora = this.dateTimeNow();

    let inicioExpediente = dataSelecionada.set({
      hour: hInicio,
      minute: mInicio,
      second: 0,
      millisecond: 0,
    });
    const fimExpedienteProfissional = dataSelecionada.set({
      hour: hFim,
      minute: mFim,
      second: 0,
      millisecond: 0,
    });

    // Agendamento normal respeita sempre o expediente do profissional — fora
    // dele (estabelecimento fechado), só é possível criar via Encaixe.
    const loopLimit = fimExpedienteProfissional;

    if (this.isSameDay(dataSelecionada, agora)) {
      const proximoHorario = this.roundToNext30Minutes(agora);
      if (proximoHorario > inicioExpediente) {
        inicioExpediente = proximoHorario;
      }
    }

    const horarios: KeyValueOption[] = [];
    let currentTime = inicioExpediente;

    while (currentTime <= loopLimit) {
      const hora = currentTime.toFormat('HH:mm');
      const { status, conflitoCom } = this.verificarStatusHorario(
        currentTime,
        profissionalId,
        duracaoNovoServico
      );

      horarios.push({
        label: hora,
        value: hora,
        active: status !== 'disponivel',
        slotStatus: status,
        conflitoCom,
        conflitoLabel:
          status === 'conflito' && conflitoCom
            ? this.translateService.instant('BARDO_AGENDA.CONFLITO_COM', {
              alvo: conflitoCom,
            })
            : undefined,
        statusLabel:
          status === 'reservado'
            ? this.translateService.instant('BARDO_AGENDA.RESERVADO')
            : status === 'almoco'
              ? this.translateService.instant('BARDO_AGENDA.INTERVALO_ALMOCO')
              : undefined,
      });
      currentTime = currentTime.plus({ minutes: 30 });
    }

    // Filtra horários ausentes e de almoço para não confundir o usuário
    this.horariosDisponiveis = horarios.filter(
      (h) => h.slotStatus !== 'ausente' && h.slotStatus !== 'almoco'
    );
  }

  private verificarStatusHorario(
    horarioDateTime: DateTime,
    profissionalId: string,
    duracaoNovoServicoMin: number
  ): { status: SlotStatus; conflitoCom?: string } {
    const profissional = this.profissionais.find(
      (prof) => String(prof.id) === String(profissionalId)
    );
    if (profissional) {
      // 1. Verificação de Ausências Parciais (Intervalos) - PRIORIDADE MÁXIMA
      const ausencias = profissional.diasAusentes || [];
      const novoEnd = horarioDateTime.plus({ minutes: duracaoNovoServicoMin });

      for (const aus of ausencias) {
        // Compara em UTC para evitar problemas de fuso no "range"
        const ausStart = LuxonNomoh.convertToUtc(aus.dataHoraInicio);
        const ausEnd = LuxonNomoh.convertToUtc(aus.dataHoraFim);
        const slotStart = horarioDateTime.toUTC();
        const slotEnd = novoEnd.toUTC();

        if (slotStart < ausEnd && slotEnd > ausStart) {
          return { status: 'ausente' };
        }
      }

      // 2. Verificação de Almoço
      const almocoStatus = this.verificarConflitoAlmoco(
        horarioDateTime,
        profissional,
        duracaoNovoServicoMin
      );
      if (almocoStatus) {
        return almocoStatus;
      }
    }

    const agendamentos =
      this.agendamentoFiltradoPorProfissional(profissionalId);

    for (const ag of agendamentos) {
      if (ag.status === StatusAgendamentoEnum.CANCELADO) continue;

      const agStart = this.toInstantDateTime(ag.dataHora).startOf('minute');
      const agDurMin = this.parseDuracaoParaMinutos(ag.duracao);
      const agEnd = agStart.plus({ minutes: agDurMin });
      const novoEnd = horarioDateTime.plus({ minutes: duracaoNovoServicoMin });

      // Regra 1: slot cai dentro da janela do agendamento existente
      if (horarioDateTime >= agStart && horarioDateTime < agEnd) {
        return { status: 'reservado' };
      }

      // Regra 2: novo serviço, se iniciado aqui, invade agendamento existente
      if (horarioDateTime < agStart && novoEnd > agStart) {
        return {
          status: 'conflito',
          conflitoCom: agStart.toFormat('HH:mm'),
        };
      }
    }

    // Verifica se o serviço ultrapassa o fim do expediente do profissional
    if (profissional?.fimExpediente) {
      const [hFim, mFim] = profissional.fimExpediente.split(':').map(Number);
      const fimExpediente = horarioDateTime.set({
        hour: hFim,
        minute: mFim,
        second: 0,
        millisecond: 0,
      });
      const novoEnd = horarioDateTime.plus({ minutes: duracaoNovoServicoMin });
      if (novoEnd > fimExpediente) {
        return {
          status: 'conflito',
          conflitoCom: profissional.fimExpediente.substring(0, 5),
        };
      }
    }

    return { status: 'disponivel' };
  }

  private verificarConflitoAlmoco(
    horarioDateTime: DateTime,
    profissional: IProfissional,
    duracaoNovoServicoMin: number
  ): { status: SlotStatus; conflitoCom?: string } | null {
    if (!profissional.almocoInicio || !profissional.almocoFim) {
      return null;
    }

    const [hInicio, mInicio] = profissional.almocoInicio.split(':').map(Number);
    const [hFim, mFim] = profissional.almocoFim.split(':').map(Number);

    const almocoInicio = horarioDateTime.set({
      hour: hInicio,
      minute: mInicio,
      second: 0,
      millisecond: 0,
    });
    const almocoFim = horarioDateTime.set({
      hour: hFim,
      minute: mFim,
      second: 0,
      millisecond: 0,
    });

    if (almocoFim <= almocoInicio) {
      return null;
    }

    if (horarioDateTime >= almocoInicio && horarioDateTime < almocoFim) {
      return { status: 'almoco' };
    }

    const novoEnd = horarioDateTime.plus({ minutes: duracaoNovoServicoMin });
    if (horarioDateTime < almocoInicio && novoEnd > almocoInicio) {
      return {
        status: 'conflito',
        conflitoCom: `${this.translateService.instant('BARDO_AGENDA.ALMOCO')} ${almocoInicio.toFormat('HH:mm')}`,
      };
    }

    return null;
  }

  private parseDuracaoParaMinutos(duracao: string | number | null): number {
    if (typeof duracao === 'number') return duracao;
    if (!duracao) return 30;
    const trimmed = duracao.trim();
    if (!trimmed) return 30;
    const numeric = Number(trimmed);
    if (Number.isFinite(numeric)) return numeric;
    const parts = trimmed.split(':').map(Number);
    if (parts.length >= 2) return parts[0] * 60 + parts[1];
    return 30;
  }
  private agendamentoFiltradoPorProfissional(
    profissionalId: string
  ): IAgendamento[] {
    const dataRaw =
      this.agendamentoForm.get('data')?.value ||
      this.AgendaDataService.$dataSelecionada();
    const dataSelecionada = this.toCalendarDateTime(dataRaw);
    const agendamentoAtualId = this.agendamento?.id;
    return (
      this.AgendaDataService.$agenda().agendamentos?.filter((agendamento) => {
        if (agendamentoAtualId && agendamento.id === agendamentoAtualId)
          return false;
        const dataAgendamento = this.toInstantDateTime(agendamento.dataHora);
        return (
          String(agendamento.profissionalId) === profissionalId &&
          this.isSameDay(dataAgendamento, dataSelecionada)
        );
      }) || []
    );
  }

  //#endregion

  //#region Salvar
  async salvarAgendamento(): Promise<void> {
    if (this.$isLoading()) return;

    if (this.agendamentoForm.invalid) {
      this.agendamentoForm.markAllAsTouched();
      this.notificacaoService.notificacaoWarn(
        'Preencha todos os campos obrigatórios'
      );
      return;
    }

    const dataSelecionada = this._dataSelecionada();
    const formValue = this.agendamentoForm.getRawValue();

    // Se o horário estiver vazio (permitido quando fechado), usa o horário atual arredondado
    const horarioFinal =
      formValue.horario ||
      this.roundToNext30Minutes(this.dateTimeNow()).toFormat('HH:mm');

    const dataHoraSelecionada = this.converterDataEHora(
      formValue.data,
      horarioFinal
    );
    const agora = new Date();

    if (dataHoraSelecionada.getTime() < agora.getTime() - 60000) {
      this.notificacaoService.notificacaoWarn(
        'Não é possível criar agendamentos em datas e horários passados.'
      );
      return;
    }

    if (dataSelecionada) {
      const dataSelecionadaLocal = this.toCalendarDateTime(formValue.data);
      const diaJs =
        dataSelecionadaLocal.weekday === 7 ? 0 : dataSelecionadaLocal.weekday;

      if (this.$diasDesabilitados().includes(diaJs)) {
        this.notificacaoService.notificacaoWarn(
          'O estabelecimento não funciona neste dia da semana.'
        );
        return;
      }

      const dataSelecionadaIso = dataSelecionadaLocal.toISODate();
      const ehFeriado = this.AgendaDataService.$feriados().some(
        (f) => f?.date === dataSelecionadaIso
      );
      if (ehFeriado && !this.estabelecimentoDataService.$estabelecimentoAtual()?.abreFeriado) {
        this.notificacaoService.notificacaoWarn(
          'O estabelecimento não abre neste feriado.'
        );
        return;
      }
    }

    this.setLoading(true);

    try {
      const formVal = this.agendamentoForm.getRawValue();

      if (formVal.pacienteId === 'novo_paciente') {
        if (!formVal.nomePacienteDependente?.trim()) {
          this.notificacaoService.notificacaoWarn(
            'Informe o nome do novo cliente dependente.'
          );

          this.setLoading(false);
          return;
        }
        if (!formVal.dataNascimentoPacienteDependente) {
          this.notificacaoService.notificacaoWarn(
            'Informe a data de nascimento do dependente.'
          );
          this.setLoading(false);
          return;
        }
        const novoDep = await this.AgendaService.cadastrarClienteRapido({
          nome: formVal.nomePacienteDependente.trim(),
          responsavelId: formVal.clientId,
          dataNascimento: formVal.dataNascimentoPacienteDependente,
        });
        if (novoDep?.id) {
          this.agendamentoForm.patchValue({ clientId: novoDep.id });
        }
      } else if (typeof formVal.pacienteId === 'string' && formVal.pacienteId.startsWith('paciente_')) {
        const depId = formVal.pacienteId.replace('paciente_', '');
        this.agendamentoForm.patchValue({ clientId: depId });
      }

      const agendamento = this.criarAgendamento();
      const isReagendamento = !!this.agendamento?.id;

      if (isReagendamento) {
        await this.AgendaService.atualizarAgendamento({
          ...agendamento,
          id: this.agendamento.id,
        });
      } else if (this.isEncaixe) {
        await this.AgendaService.inserirEncaixe(agendamento);
        await this.AgendaService.buscarAgenda();
      } else {
        await this.AgendaService.inserirAgendamento(agendamento);
        await this.AgendaService.buscarAgenda();
      }

      if (agendamento.dataHora) {
        const noFuso = toInstantDateTime(
          agendamento.dataHora,
          this.getTimeZoneEstabelecimento()
        );
        if (noFuso) {
          this.AgendaDataService.setDataSelecionada(
            this.toBrowserCalendarDate(noFuso)
          );
        }
      }

      this.fecharModal();
      this.agendamentoSalvo.emit();
    } catch {
      // interceptor handles error notification
    } finally {
      this.setLoading(false);
    }
  }

  private criarAgendamento(): NovoAgendamentoPayload {
    const formValue = this.agendamentoForm.getRawValue();
    const agendaId = this.AgendaDataService.$agenda().id;

    if (!agendaId) {
      throw new Error('Agenda não carregada. Recarregue e tente novamente.');
    }

    const telefoneEstabelecimento =
      this.estabelecimentoDataService.$estabelecimentoAtual()?.telefone;

    return {
      agendaId,
      dataHora: this.converterDataEHora(
        formValue.data,
        formValue.horario ||
        this.roundToNext30Minutes(this.dateTimeNow()).toFormat('HH:mm')
      ),
      telefoneCliente: this.limparTelefone(formValue.telefone),
      nomeCliente: (formValue.nome || '').trim(),
      servicoId: formValue.servico,
      profissionalId: formValue.profissional,
      clienteId: formValue.clientId,
      status: StatusAgendamentoEnum.PENDENTE,
      observacao: formValue.observacoes || undefined,
      verificarDisponibilidade: false,
      telefoneEstabelecimento,
      isEncaixe: this.isEncaixe,
      isDomicilio: formValue.isDomicilio === true,
      endereco:
        formValue.isDomicilio === true
          ? String(formValue.endereco ?? '').trim()
          : null,
    };
  }

  private atualizarOpcoesDomicilio(): void {
    this.opcoesDomicilio = [
      { label: this.translateService.instant('GLOBAL.SIM'), value: true },
      { label: this.translateService.instant('GLOBAL.NAO'), value: false },
    ];
  }

  private atualizarValidacaoEndereco(
    isDomicilio: boolean,
    limparEndereco: boolean
  ): void {
    const enderecoControl = this.agendamentoForm.get('endereco');
    if (!enderecoControl) return;

    if (isDomicilio) {
      enderecoControl.setValidators([
        Validators.required,
        Validators.maxLength(500),
      ]);
      if (limparEndereco && !this.$isReagendamento()) {
        enderecoControl.setValue(this.enderecoClienteSelecionado, {
          emitEvent: false,
        });
        enderecoControl.enable({ emitEvent: false });
      }
    } else {
      enderecoControl.setValidators([Validators.maxLength(500)]);
      if (limparEndereco) {
        enderecoControl.setValue(null, { emitEvent: false });
      }
    }

    enderecoControl.updateValueAndValidity({ emitEvent: false });
  }
  //#endregion

  //#region Modal
  fecharModal(): void {
    this.enderecoClienteSelecionado = null;
    this.agendamentoForm.reset();
    this.$continuarAgendamento.set(false);
    this.$desabilitarBotaoAdicionarCliente.set(true);
    this._servicoSelecionadoId.set(null);
    this._profissionalSelecionadoId.set(null);
    this._dataSelecionada.set(null);
    this.horariosDisponiveis = [];
    this.inicializarForm();
    this.configurarListeners();
    this.sincronizarDataSelecionada();
  }
  //#region Métodos Seletor Horário Encaixe
  abrirModalSeletorHorario(): void {
    const control = this.agendamentoForm?.get('horario');
    if (!control) return;

    if (control.disabled) {
      if (this.isEncaixe) {
        control.enable();
      } else {
        this.notificacaoService.notificacaoWarn(
          'Selecione o profissional antes de escolher o horário.'
        );
        return;
      }
    }

    const val = control.value;
    if (val instanceof Date && !isNaN(val.getTime())) {
      this.horarioTemporarioEncaixe = new Date(val);
    } else if (typeof val === 'string' && val.includes(':')) {
      const [h, m] = val.split(':').map(Number);
      const d = new Date();
      d.setHours(h || 0, m || 0, 0, 0);
      this.horarioTemporarioEncaixe = d;
    } else {
      const agora = new Date();
      agora.setSeconds(0, 0);
      this.horarioTemporarioEncaixe = agora;
    }

    this.$modalSeletorHorarioVisible.set(true);
  }

  confirmarHorarioEncaixe(): void {
    const control = this.agendamentoForm?.get('horario');
    if (control) {
      control.setValue(this.horarioTemporarioEncaixe);
      control.markAsDirty();
      control.markAsTouched();
    }
    this.$modalSeletorHorarioVisible.set(false);
    this.removerFocoGeral();
  }

  fecharModalSeletorHorario(): void {
    this.$modalSeletorHorarioVisible.set(false);
    this.removerFocoGeral();
  }

  removerFocoInput(event?: Event): void {
    const target = event?.target as HTMLElement | undefined;
    target?.blur();
    this.removerFocoGeral();
  }

  private removerFocoGeral(): void {
    setTimeout(() => {
      const activeEl = document.activeElement as HTMLElement | null;
      if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || activeEl.tagName === 'SELECT')) {
        activeEl.blur();
      }
    }, 50);
  }

  obterHorarioFormatadoEncaixe(): string {
    const val = this.agendamentoForm?.get('horario')?.value;
    if (!val) return '';
    if (val instanceof Date && !isNaN(val.getTime())) {
      const hours = String(val.getHours()).padStart(2, '0');
      const minutes = String(val.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    }
    if (typeof val === 'string') {
      return val;
    }
    return '';
  }
  //#endregion

  //#region Utilities
  /**
   * Retorna o conjunto de servicoId que o profissional logado presta, ou
   * `null` quando o perfil não é PROFISSIONAL (ADMINISTRADOR/RECEPCAO não
   * são restritos e continuam vendo todas as categorias/serviços).
   */
  private calcularMeusServicoIds(
    profissionais: IProfissional[],
    meuProfissionalId: string | null
  ): Set<string> | null {
    if (!meuProfissionalId) {
      return null;
    }
    const meuProfissional = profissionais.find(
      (profissional) => String(profissional.id) === meuProfissionalId
    );
    return new Set(
      (meuProfissional?.servicos || []).map((servico) =>
        String(servico.servicoId)
      )
    );
  }

  private setLoading(value: boolean): void {
    this.$isLoading.set(value);
    this.loadingChange.emit(value);
  }

  private validarTelefone(telefone: string): boolean {
    const digits = String(telefone ?? '').replace(/\D/g, '');
    return digits.length >= 10;
  }

  private limparTelefone(telefone: string): string {
    return telefone.replace(/\D/g, '');
  }

  private sincronizarDataSelecionada(): void {
    const dataSelecionada = this.AgendaDataService.$dataSelecionada();
    if (dataSelecionada) {
      this.atualizarDataForm(dataSelecionada);
    }
  }

  private atualizarDataForm(data: DateInput): void {
    if (!this.agendamentoForm) return;
    const dateTime = this.toCalendarDateTime(data);
    const dateObj = this.toBrowserCalendarDate(dateTime);
    this._dataSelecionada.set(dateObj);
    const formValue = this.deviceService.$isMobile()
      ? dateTime.toFormat('yyyy-MM-dd')
      : dateObj;
    this.agendamentoForm.get('data')?.setValue(formValue, { emitEvent: false });
  }
  private converterDataEHora(data: string | Date, hora: string | Date): Date {
    let dia: number, mes: number, ano: number;
    if (typeof data === 'string') {
      if (data.includes('-')) {
        // ISO format "YYYY-MM-DD" from native date input
        [ano, mes, dia] = data.split('-').map(Number);
      } else {
        // "DD/MM/YYYY" format from masked input
        [dia, mes, ano] = data.split('/').map(Number);
      }
    } else {
      dia = data.getDate();
      mes = data.getMonth() + 1;
      ano = data.getFullYear();
    }
    // Encaixe usa p-datepicker[timeOnly] (Input `horario` chega como Date);
    // o fluxo normal usa p-select/lib-n-multiselect (chega como string "HH:mm").
    const [horas, minutos] =
      hora instanceof Date
        ? [hora.getHours(), hora.getMinutes()]
        : hora.split(':').map(Number);
    return DateTime.fromObject(
      {
        year: ano,
        month: mes,
        day: dia,
        hour: horas,
        minute: minutos,
        second: 0,
        millisecond: 0,
      },
      { zone: this.getTimeZoneEstabelecimento() }
    )
      .toUTC()
      .toJSDate();
  }

  private toCalendarDateTime(value: DateInput): DateTime {
    if (!value) {
      return this.dateTimeNow();
    }

    if (DateTime.isDateTime(value)) {
      return (value as DateTime).setZone(this.getTimeZoneEstabelecimento());
    }

    if (value instanceof Date) {
      return DateTime.fromObject(
        {
          year: value.getFullYear(),
          month: value.getMonth() + 1,
          day: value.getDate(),
          hour: value.getHours(),
          minute: value.getMinutes(),
          second: value.getSeconds(),
          millisecond: value.getMilliseconds(),
        },
        { zone: this.getTimeZoneEstabelecimento() }
      );
    }

    if (typeof value === 'string') {
      const hasZoneInfo = /Z|[+-]\d{2}:\d{2}$/i.test(value);
      return hasZoneInfo
        ? DateTime.fromISO(value).setZone(this.getTimeZoneEstabelecimento())
        : DateTime.fromISO(value, { zone: this.getTimeZoneEstabelecimento() });
    }

    return this.dateTimeNow();
  }

  private toInstantDateTime(value: DateInput): DateTime {
    if (!value) {
      return this.dateTimeNow();
    }

    if (DateTime.isDateTime(value)) {
      return (value as DateTime).setZone(this.getTimeZoneEstabelecimento());
    }

    if (value instanceof Date) {
      return DateTime.fromJSDate(value).setZone(
        this.getTimeZoneEstabelecimento()
      );
    }

    const hasZoneInfo = /Z|[+-]\d{2}:\d{2}$/i.test(value);
    return hasZoneInfo
      ? DateTime.fromISO(value).setZone(this.getTimeZoneEstabelecimento())
      : DateTime.fromISO(value, { zone: this.getTimeZoneEstabelecimento() });
  }

  private dateTimeNow(): DateTime {
    return DateTime.now().setZone(this.getTimeZoneEstabelecimento());
  }

  private toBrowserCalendarDate(dateTime: DateTime): Date {
    return new Date(dateTime.year, dateTime.month - 1, dateTime.day, 12);
  }

  private getTimeZoneEstabelecimento(): string {
    const timeZone =
      this.estabelecimentoDataService
        .$estabelecimentoAtual()
        ?.timeZone?.trim() ||
      this.timeZonePadrao;
    return DateTime.now().setZone(timeZone).isValid
      ? timeZone
      : this.timeZonePadrao;
  }

  private roundToNext30Minutes(value: DateTime): DateTime {
    const sanitized = value.set({ second: 0, millisecond: 0 });
    const remainder = sanitized.minute % 30;
    const needsIncrement =
      remainder !== 0 || value.second !== 0 || value.millisecond !== 0;
    const minutesToAdd = needsIncrement
      ? remainder === 0
        ? 30
        : 30 - remainder
      : 0;

    return sanitized.plus({ minutes: minutesToAdd });
  }
  private isSameDay(first: DateTime, second: DateTime): boolean {
    return first.hasSame(second, 'day');
  }

  private formatarDuracaoLabel(duracao: string | number): string {
    const minutos = this.parseDuracaoParaMinutos(duracao);
    if (minutos < 60) return `${minutos}min`;
    const h = Math.floor(minutos / 60);
    const m = minutos % 60;
    return m === 0 ? `${h}h` : `${h}h${m}min`;
  }

  private formatarTelefoneBd(tel: string | null | undefined): string {
    // lib-phone-input recebe o valor raw (com ou sem DDI) e formata internamente
    return tel ?? '';
  }

  //#endregion
}
